'use client';

import { useState } from 'react';

type FormData = {
  name: string;
  phone: string;
  email: string;
  documentType: string;
  preferredDate: string;
  preferredTime: string;
  location: string;
  notes: string;
};

const initialForm: FormData = {
  name: '',
  phone: '',
  email: '',
  documentType: '',
  preferredDate: '',
  preferredTime: '',
  location: '',
  notes: '',
};

const documentTypes = [
  { value: 'general', label: 'General Notarization' },
  { value: 'loan-purchase', label: 'Loan Signing — Purchase' },
  { value: 'loan-refinance', label: 'Loan Signing — Refinance / HELOC' },
  { value: 'loan-seller', label: 'Loan Signing — Seller Documents' },
  { value: 'real-estate', label: 'Real Estate Documents' },
  { value: 'poa', label: 'Power of Attorney' },
  { value: 'i9', label: 'I-9 Verification' },
  { value: 'hospital', label: 'Hospital / Jail Signing' },
  { value: 'other', label: 'Other (describe in notes)' },
];

const timeSlots = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM',
];

const CALENDLY_URL = 'https://calendly.com/918cap';

export default function ContactSection() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="bg-navy-mid py-24 md:py-40">
      <div className="max-w-editorial mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">

          {/* Left: context */}
          <div className="lg:col-span-4 space-y-10">
            <div>
              <p className="section-label mb-4">Book Your Appointment</p>
              <div className="editorial-divider" />
              <h2 className="font-fraunces text-4xl md:text-5xl font-700 text-cream tracking-tight leading-tight">
                Schedule your signing.
              </h2>
            </div>

            <p className="font-dm text-[0.9rem] text-slate leading-relaxed">
              Fill out the form and we&apos;ll confirm within 60 minutes during business hours.
              For urgent same-day requests, call directly — we answer.
            </p>

            {/* Tap-to-dial */}
            <div className="bg-gold rounded-[6px] p-7 space-y-3">
              <p className="font-dm text-[0.65rem] font-700 uppercase tracking-[0.25em] text-navy/60">
                Urgent / Same-Day
              </p>
              <a
                href="tel:+14695550183"
                className="font-fraunces text-3xl font-600 text-navy-dark hover:text-navy transition-colors block"
              >
                (469) 555-0183
              </a>
              <p className="font-dm text-[0.78rem] text-navy/60 leading-relaxed">
                Available 7 days a week, including evenings and weekends.
                Same-day requests welcome — call for availability.
              </p>
            </div>

            {/* Calendly link */}
            <div className="p-6 rounded-lg border border-gold/20 bg-gold-faint">
              <p className="font-dm text-[0.72rem] font-700 uppercase tracking-[0.2em] text-gold mb-3">
                Prefer to self-schedule?
              </p>
              <p className="font-dm text-[0.83rem] text-slate leading-relaxed mb-4">
                Use our Calendly booking page to pick a time that works for you.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-dm text-[0.75rem] font-700 uppercase tracking-[0.15em] text-gold hover:text-gold-light transition-colors"
              >
                Open Calendly
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>

            {/* What happens next */}
            <div className="space-y-4">
              <p className="font-dm text-[0.65rem] font-700 uppercase tracking-[0.25em] text-slate">
                After you submit
              </p>
              {[
                'Confirmation call or text within 60 minutes',
                'Notary assigned to your appointment',
                'Document checklist emailed to you',
                'Notary arrives at your location on time',
              ].map((step, i) => (
                <div key={step} className="flex items-start gap-4">
                  <span className="font-fraunces text-sm font-700 text-gold mt-0.5 w-5 flex-shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="font-dm text-[0.82rem] text-slate leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div className="bg-navy-light rounded-[6px] p-12 md:p-16 text-center space-y-6 h-full flex flex-col items-center justify-center min-h-[500px] border border-gold/20">
                <div className="w-16 h-16 rounded-full border-2 border-gold flex items-center justify-center mx-auto">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 13l4 4L19 7" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="font-fraunces text-3xl font-700 text-cream tracking-tight">
                  Request received.
                </h3>
                <p className="font-dm text-[0.9rem] text-slate max-w-sm leading-relaxed">
                  We&apos;ll confirm your appointment within 60 minutes. Check your phone for a text or call from us.
                </p>
                <p className="font-dm text-[0.78rem] text-slate/60">
                  Need immediate help? Call{' '}
                  <a href="tel:+14695550183" className="text-gold hover:underline">
                    (469) 555-0183
                  </a>
                </p>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-secondary mt-2"
                >
                  Also book via Calendly
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="form-label">
                    Full Name <span className="text-gold">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Jane Smith"
                    className="form-input"
                  />
                </div>

                {/* Contact row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="form-label">
                      Phone <span className="text-gold">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="(469) 555-0100"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="form-label">
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@email.com"
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Document type */}
                <div>
                  <label htmlFor="documentType" className="form-label">
                    Document Type <span className="text-gold">*</span>
                  </label>
                  <select
                    id="documentType"
                    name="documentType"
                    value={form.documentType}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="" disabled>Select document type</option>
                    {documentTypes.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Date + Time */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="preferredDate" className="form-label">
                      Preferred Date <span className="text-gold">*</span>
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={form.preferredDate}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredTime" className="form-label">
                      Preferred Time <span className="text-gold">*</span>
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={form.preferredTime}
                      onChange={handleChange}
                      required
                      className="form-input"
                    >
                      <option value="" disabled>Select time</option>
                      {timeSlots.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="form-label">
                    Signing Location / City <span className="text-gold">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    required
                    placeholder="123 Main St, Dallas, TX 75201 (or just city)"
                    className="form-input"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="form-label">
                    Additional Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Number of documents, special instructions, hospital name, etc."
                    className="form-input resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button type="submit" className="cta-primary flex-1 justify-center">
                    Submit Request
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-secondary flex-1 justify-center text-center"
                  >
                    Book via Calendly
                  </a>
                </div>

                <p className="font-dm text-[0.72rem] text-slate/60 text-center">
                  We&apos;ll confirm within 60 minutes during business hours. For urgent requests, call{' '}
                  <a href="tel:+14695550183" className="text-gold hover:underline">(469) 555-0183</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
